import {
  CalendarDays,
  Package,
  RotateCcw,
  ShieldCheck,
  Truck,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import EligibilityBadge from "@/views/shop/sections/EligibilityBadge";
import { CONFIGURATOR, type MaticModelId } from "@/views/shop/shopContent";

type Assurance =
  (typeof CONFIGURATOR.assurances)[number] | typeof CONFIGURATOR.sereneShipping;

const ASSURANCE_ICONS: Record<Assurance["id"], LucideIcon> = {
  "risk-free": RotateCcw,
  shipping: Package,
  warranty: ShieldCheck,
  returns: Truck,
  "serene-shipping": CalendarDays,
};

/** The promises, with Serene's ship date in place of "Ships in 2 weeks". */
const ASSURANCES_WITH_SERENE: readonly Assurance[] =
  CONFIGURATOR.assurances.map((assurance): Assurance =>
    assurance.id === "shipping" ? CONFIGURATOR.sereneShipping : assurance,
  );

/** The promises for a model: Serene's ship date only while it's picked. */
const assurancesFor = (modelId: MaticModelId): readonly Assurance[] =>
  modelId === "matic-serene" ? ASSURANCES_WITH_SERENE : CONFIGURATOR.assurances;

/**
 * One promise in the configurator's closing style: a 1.5-stroke line icon
 * and the copy in regular weight. Stacked, the icon sits over the two
 * authored lines in `text-assurance`; inline, beside them joined on one line
 * in the 12px `text-side-cart-assurance`.
 */
const AssuranceItem = ({
  assurance,
  layout,
}: {
  assurance: Assurance;
  layout: "stacked" | "inline";
}) => {
  const Icon = ASSURANCE_ICONS[assurance.id];
  return (
    <>
      <Icon
        aria-hidden="true"
        className={cn(
          "shrink-0 text-content-primary",
          layout === "stacked" ? "size-[25px]" : "size-[16px]",
        )}
        strokeWidth={1.5}
      />
      <span
        className={cn(
          "font-ppMori font-normal text-content-primary",
          layout === "stacked"
            ? "text-assurance"
            : "whitespace-nowrap text-side-cart-assurance",
        )}
      >
        {layout === "stacked" ? (
          <>
            {assurance.lines[0]}
            {/* Keeps the lines apart in the accessible name. */} <br />
            {assurance.lines[1]}
          </>
        ) : (
          assurance.lines.join(" ")
        )}
      </span>
    </>
  );
};

/**
 * The same assurances as one quiet line over the visual zone, after Eight
 * Sleep's pod-cover page, in the configurator's closing style: each icon
 * 4px from its words, 24 between items (40 from `d`), centred; 17px under
 * the nav and about 24 above the image. Phones leave it out, as Eight Sleep's do (the
 * same assurances close the configurator). Serene's ship date stays out of
 * it; the closing row and Serene's model card carry it.
 */
export const AssuranceStrip = ({ className }: { className: string }) => (
  <ul
    aria-label={CONFIGURATOR.assurancesLabel}
    className={cn(
      // At 12px the four run 620 wide 24 apart, so they hold one line from
      // `tab` (736 inside the margins at 800) and in the image column at its
      // narrowest (653 at 1280); the wrap, 4 under, is only a fallback for
      // a longer line of copy. From `sd` the line sits in the 56px band from
      // its 11px top; from `d`, Eight Sleep's 40 apart.
      "hidden w-full flex-wrap items-start justify-center gap-x-l gap-y-[4px] pt-[17px] tab:flex sd:h-[var(--assurance-strip-band)] sd:pt-[11px] d:flex-nowrap d:gap-x-xxl",
      className,
    )}
  >
    {CONFIGURATOR.assurances.map((assurance) => (
      <li key={assurance.id} className="flex shrink-0 items-center gap-[4px]">
        <AssuranceItem assurance={assurance} layout="inline" />
      </li>
    ))}
  </ul>
);

/** The assurance row and HSA/FSA badge that close the configurator column. */
export const ConfiguratorAssurances = ({
  modelId,
}: {
  modelId: MaticModelId;
}) => (
  <>
    {/* The CTA bar stays a direct child so it sticks for the whole column;
        the negative margin cancels the column gap and the bar's bottom
        padding, leaving 48 from the bar's card. */}
    <div className="-mt-[calc(16px+var(--grid-margin))] flex flex-col gap-[48px] sd:mt-[8px]">
      {/* Fifths of the tablet's full width: the four sit centred on one
          row. In the narrow column (phones, and the 420 column from `sd`),
          thirds: three, then the last one centred under them. */}
      <div className="flex flex-wrap justify-center gap-x-[8px] gap-y-l">
        {assurancesFor(modelId).map((assurance) => (
          <div
            key={assurance.id}
            className="flex w-[calc((100%-16px)/3)] flex-col items-center gap-[8px] text-center tab:w-[calc((100%-32px)/5)] sd:w-[calc((100%-16px)/3)]"
          >
            <AssuranceItem assurance={assurance} layout="stacked" />
          </div>
        ))}
      </div>

      <EligibilityBadge />
    </div>
  </>
);
